import { ReactNode, useSyncExternalStore } from "react";
import reactStringReplace from "react-string-replace";
import { en } from "./en";
import { es } from "./es";
import { ru } from "./ru";
// Canonical locale list — plain JSON so it can also be imported from the
// Node-evaluated app.config.ts (`@linqx/i18n/locales`) without pulling in React.
import locales from "./locales.json";

export type { TranslationKey, Dict } from "./types";

export interface LanguageKeys {
  [key: string]: string;
}
export interface Languages {
  [key: string]: LanguageKeys;
}

const DEFAULT_LOCALE = locales.default;

/** All supported locale codes, e.g. ["en", "es", "ru"]. */
export const SUPPORTED_LOCALES = locales.supported;

const languages: Languages = { en, es, ru };

/**
 * Normalize any BCP-47 tag (e.g. "es-MX", "ru-RU", "en-US") or bare code to a
 * supported locale, falling back to English. Safe to call with undefined.
 */
export const resolveLocale = (raw?: string | null): string => {
  const base = (raw ?? "").toLowerCase().split(/[-_]/)[0];
  return languages[base] ? base : DEFAULT_LOCALE;
};

class LanguageInternal {
  private locale = DEFAULT_LOCALE;
  private listeners = new Set<() => void>();

  public getLocale = () => this.locale;

  /** Available locale codes (e.g. for a language picker). */
  public getAvailableLocales = (): string[] => SUPPORTED_LOCALES;

  /**
   * Set the active locale (accepts BCP-47 tags — normalized via
   * `resolveLocale`) and notify subscribers so the UI can re-render.
   */
  public setLocale = (locale: string) => {
    const next = resolveLocale(locale);
    if (next === this.locale) return;
    this.locale = next;
    this.listeners.forEach((l) => l());
  };

  /** Subscribe to locale changes; returns an unsubscribe function. */
  public subscribe = (listener: () => void): (() => void) => {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  };

  public replaceVarsReact = (str: any, data: any = {}): ReactNode[] => {
    Object.keys(data).forEach((key: string) => {
      let cb = () => data[key];
      if (typeof data[key] === "function") {
        cb = data[key];
      }
      str = reactStringReplace(str, `{${key}}`, cb);
    });
    return str;
  };

  public replaceVarsString = (
    value: string,
    variables: { [key: string]: string },
  ): string => {
    Object.keys(variables).forEach((key) => {
      value = value.replace(new RegExp(`{${key}}`, "g"), variables[key]);
    });
    return value;
  };

  /**
   * Resolve a key for the active locale. Falls back to English when the key
   * is missing in the active locale (never shows a half-translated screen),
   * then to the raw key unless `noFallback` is set.
   */
  getKey = (key: string, noFallback?: boolean): string => {
    const value: string | undefined =
      languages[this.locale]?.[key] ?? languages[DEFAULT_LOCALE][key];
    if (value === undefined) {
      return noFallback ? "" : key;
    }
    return value;
  };
}

export const Language = new LanguageInternal();

/**
 * React hook: returns the active locale and re-renders the caller whenever it
 * changes. Mount at each surface's root so a locale switch reflows the whole
 * tree (every `Language.getKey(...)` call then reads the new strings).
 */
export const useLocale = (): string =>
  useSyncExternalStore(
    Language.subscribe,
    Language.getLocale,
    Language.getLocale,
  );

/**
 * Web-only: pick the locale from the URL `?lang=` param (used by the native
 * shell to forward the app's language into a mini-app WebView), falling back
 * to the browser language, then English. No-op outside the browser.
 */
export const applyLocaleFromUrl = (): void => {
  if (typeof window === "undefined") return;
  const fromUrl = new URLSearchParams(window.location.search).get("lang");
  const fromBrowser =
    typeof navigator !== "undefined" ? navigator.language : undefined;
  Language.setLocale(resolveLocale(fromUrl ?? fromBrowser));
};

/**
 * Stateless translation for a specific locale — safe for concurrent server-side
 * use (per-recipient emails/push), unlike the mutable `Language` singleton.
 * Falls back to English, then the raw key. Optional `{var}` interpolation.
 */
export const translate = (
  locale: string,
  key: string,
  vars?: { [k: string]: string },
): string => {
  const l = resolveLocale(locale);
  let value = languages[l]?.[key] ?? languages[DEFAULT_LOCALE][key] ?? key;
  if (vars) {
    Object.keys(vars).forEach((k) => {
      value = value.replace(new RegExp(`{${k}}`, "g"), vars[k]);
    });
  }
  return value;
};

/**
 * Display label for a catalog tag. Catalog tags store a stable `key` slug in
 * the DB; the human label is translated here from `tag_<key>`, falling back to
 * the English `title` when there is no translation (or no key).
 */
export const tagLabel = (tag: {
  key?: string | null;
  title: string;
}): string => {
  const translated = tag.key ? Language.getKey(`tag_${tag.key}`, true) : "";
  return translated || tag.title;
};

// Back-compat: the flat English table used to be exported as `keys`.
export { en as keys } from "./en";
